import { useCurrentDashboard } from "components/Signals/useCurrentDashboard";
import SmallCardStore from "./SmallCardStore";
import { useEffect, useState } from "react";
import { useStore } from "utils/useStore";
import { styleClsx } from "utils/styleClsx";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { Box, IconButton, useTheme } from "@mui/material";
import AcxMenu, { type AcxMenuItemProps } from "components/UI/Menu/AcxMenu";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxButton from "components/UI/AcxButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { observer } from "mobx-react";
import {
    defaultDrawerOffset,
    LayoutDrawerStore,
} from "stores/Layout/LayoutDrawerStore";
import ObjectConfigurationDrawer from "../ObjectConfiguration/ObjectConfigurationDrawer";
import { useObjectMenu } from "../useObjectMenu";

export const SmallCardMenu = observer(({
    store,
    testId,
}: {
    store: SmallCardStore;
    testId: string;
}) => {
    const currentDashboard = useCurrentDashboard();
    const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
    const [isDeleteLoading, setDeleteIsLoading] = useState(false);
    const messageStore = useStore(MessageStore);
    const drawerStore = useStore(LayoutDrawerStore);
    const theme = useTheme();
    const {
        menuAnchor,
        isMenuOpen,
        setIsMenuOpen,
        handleMenuButtonClick,
        handleMenuButtonKeyDown,
    } = useObjectMenu();
    const canConfigure = store.supportsObjectFilters() && store.isEditable;
    const isRefreshing = store.isRefreshing;

    const openConfigDrawer = () => {
        drawerStore.closeAndResetDrawer();
        drawerStore.restoreDefaults();
        drawerStore.setOffsetPixels(defaultDrawerOffset);
        drawerStore.setCustomRootStyles({
            zIndex: theme.zIndex.appBar - 1,
        });
        drawerStore.setContentFactory(() => (
            <ObjectConfigurationDrawer store={store} />
        ));
        drawerStore.setOpen(true);
    };

    const menuItems: Array<AcxMenuItemProps | "Divider"> = [
        {
            id: `${testId}-menu-refresh`,
            label: "Refresh",
            props: {
                onClick: () => {
                    void store.runRefresh(true);
                    setIsMenuOpen(false);
                },
                disabled: isRefreshing,
            },
        },
        ...(canConfigure
            ? [
                  {
                      id: `${testId}-menu-configure`,
                      label: "Configure",
                      props: {
                          onClick: () => {
                              openConfigDrawer();
                              setIsMenuOpen(false);
                          },
                          disabled: isRefreshing,
                      },
                  } as AcxMenuItemProps,

              ]
            : []),
        ...(store.isDeletable
            ? [
                  "Divider" as const,
                  {
                      id: `${testId}-menu-delete`,
                      label: "Delete",
                      props: {
                          onClick: () => {
                              setIsConfirmDeleteOpen(true);
                              setIsMenuOpen(false);
                          },
                      },
                  },
              ]
            : []),
    ];

    useEffect(() => {
        if (!isRefreshing) return;
        setIsMenuOpen(false);
    }, [isRefreshing, setIsMenuOpen]);

    return (
        <>
            {!currentDashboard?.isPresentationMode &&
                currentDashboard &&
                (store.isEditable || store.isDeletable) && (
                    <IconButton
                        ref={menuAnchor}
                        data-testid={`${testId}-menu-button`}
                        id={`${store.title}-small-card-menu`}
                        style={styleClsx({
                            color: isRefreshing
                                ? theme.palette.gray[400]
                                : theme.palette.black.main,
                            height: "34px",
                            cursor: isRefreshing ? "default" : "pointer",
                        })}
                        onClick={handleMenuButtonClick}
                        onKeyDown={handleMenuButtonKeyDown}
                        size="small"
                        disabled={isRefreshing}
                    >
                        <MoreVertIcon />
                    </IconButton>
                )}
            <AcxMenu
                open={isMenuOpen}
                anchorElement={isMenuOpen ? menuAnchor.current : undefined}
                onMenuClose={() => setIsMenuOpen(false)}
                disableAutoFocusItem
                menuItems={menuItems}
            />
            <AcxDialog
                isOpen={isConfirmDeleteOpen}
                onClose={() => setIsConfirmDeleteOpen(false)}
                title="Confirm Delete Small Card"
                dialogContentChildren={
                    <Box>Are you sure you want to delete this small card?</Box>
                }
                maxWidth={false}
            >
                <AcxButton
                    data-testid={`${testId}-confirm-delete`}
                    onClick={async () => {
                        try {
                            if (!store.id) return;
                            setDeleteIsLoading(true);
                            await currentDashboard?.deleteSignalsObjectById(
                                store.id,
                            );
                            setIsConfirmDeleteOpen(false);
                        } catch (e) {
                            messageStore.logError(
                                "Failed to delete small card",
                            );
                        }
                        setDeleteIsLoading(false);
                    }}
                    loading={isDeleteLoading}
                >
                    Delete
                </AcxButton>
            </AcxDialog>
        </>
    );
});
